// FIXME: Merge constant scan resolution maps into a single map with named keys.

// Internal texture size codes in Bach and Shopfront use lower case k. UI uses upper case K.
export const SCAN_RESOLUTIONS = {
  '1k': 1024,
  '2k': 2048,
  '4k': 4096,
  '6k': 6144,
  '8k': 8192,
}

export const SCAN_RESOLUTION_DEFAULT = '4k';
export const TEX_SCALE_DEFAULT = SCAN_RESOLUTIONS[SCAN_RESOLUTION_DEFAULT];

// boundaryMode is absent on legacy materials; treat those as non-synth.
export function isSynthBoundaryMode(material) {
  const tilingParams = material?.tilingParams;
  return tilingParams?.hasOwnProperty('boundaryMode')
    ? tilingParams.boundaryMode === 'synth'
    : false;
}

export const PROCESSING_TIME_HOURS = {
  '2k': 0.5,
  '4k': 2,
  '6k': 3,
}

export const MINIMUM_MEGAPIXELS = {
  '2k': 18,
  '4k': 24,
  '6k': 45,
}

export const ScanType = Object.freeze({
  CAMERA_SCAN: 'cameraScan',
  TEXTURE_IMPORT: 'textureImport',
  STITCHED_MATERIAL: 'stitchedMaterial',
});

// File extensions accepted for upload, by ScanType. Shared between the upload UI
// (components/PhotoUpload.js, for Uppy restrictions and the on-screen format list) and the
// upload API (api/upload.js, for server-side filename validation) so the two never drift apart.
//
// Keep synchronised with RAW_EXTS/NONRAW_EXTS in Bach:scripts/bach.py (camera scan) and the
// formats handled by Bach:scripts/texin.py (texture import).
export const ScanTypeFileExtensions = Object.freeze({
  [ScanType.CAMERA_SCAN]: [
    '.ARW',
    '.CR2',
    '.CR3',
    '.DNG',
    '.NEF',
    '.RAF',
    '.RW2',
    // '.TIF',
    // '.TIFF',
    // '.JPG'   // For testing only
  ],
  [ScanType.TEXTURE_IMPORT]: [
    '.PNG',
    '.JPG',
    '.JPEG',
    '.EXR',
    '.TIF',
    '.TIFF',
  ],
});

export const Integrations = Object.freeze({
  'dmix': 'DMIx',
});

export const TEXTURE_FORMAT_DEFAULTS = {
  pixelFormat: 'u8_png',
  normalFormat: 'opengl',
  diffuseType: 'auto',
  diffuseTypes: [],
};

export function getMaterialScanType(material) {
  const { scanType, stitched } = material;
  for (const value of Object.values(ScanType)) {
    if (scanType === value) {
      return value;
    }
  }
  if (stitched) {
    return ScanType.STITCHED_MATERIAL;
  } else {
    return ScanType.CAMERA_SCAN;
  }
}

export function getBrdfConfig(mSet) {
  try {
    return mSet.materials[0].brdf.config;
  } catch (e) {
    return null;
  }
}

export function setBrdfConfig(mSet, brdfConfig) {
  try {
    const brdf = mSet.materials[0].brdf;
    brdf['config'] = brdfConfig;
  } catch (e) {
    return null;
  }
}

export function getGalleryState(mSet) {
  try {
    const gallery = mSet.materials[0].gallery[0];
    const state = gallery['config']['renders'][gallery['name']]['state'];
    return state;
  } catch (e) {
    return null;
  }
}

export function setGalleryState(mSet, galleryState) {
  try {
    const gallery = mSet.materials[0].gallery[0];
    const render = gallery['config']['renders'][gallery['name']]
    render['state'] = galleryState;
  } catch (e) {
    return null;
  }
}

export function mergeState(state, targetState, excludedFields=null) {
  // Merge fields other than excluded fields of state into targetState
  Object.keys(state).map(key => {
    if (!excludedFields || !excludedFields.includes(key)) {
      targetState[key] = state[key];
    }
  });

  return targetState;
}

// Gallery state fields to exclude when merging state into a preview material
export const excludedStatePreviewMat = [
  'areaLightHeight',
  'areaLightWidth',
  'autoRotateCamFactor',
  'autoRotateFps',
  'autoRotateLightFactor',
  'autoRotatePeriodMs',
  'bloom',
  'camTiltLimitDegrees',
  'camTiltWithDeviceOrient',
  'camTiltWithMousePos',
  'cameraPan',
  'currentZoom',
  'displacementOffset',
  'displacementUnits',
  'dragControlsPanning',
  'dragControlsRotation',
  'lightTiltLimitDegrees',
  'lightTiltWithDeviceOrient',
  'lightTiltWithMousePos',
  'lightType',
  'lightControl',
  'meshOverride',
  'metresPerPixel',
  'metresPerPixelTextures',
  'stretch',
  'texDims',
  'texDimsActual',
  'zoom',
  // All baking parameters are also excluded, to avoid "double baking" in tiled preview shimmer
  'colorTemperature',
  'hue',
  'meshRotateZDegrees',
  'saturation',
  // Preserve diffuse selection/types from freshly generated target state.
  'diffuseType',
  'diffuseTypes',
];

// Gallery state fields to exclude when merging state into an ordinary material
export const excludedStateGalleryMat = ['brdfModel', 'brdfVersion', 'illumL', 'metresPerPixel',
'metresPerPixelTextures', 'texSize', 'texDims', 'texDimsActual', 'yFlip', 'displacementOffset',
'displacementUnits', 'diffuseType', 'diffuseTypes'];

// Merge gallery-related state from a source material set object into a target material set object.
export async function addGallerySettings(matSetSource, matSetTarget, isPreview=false) {
  try {
    const sourceState = matSetSource.materials[0].gallery[0].config.renders['0'].state;
    const targetState = matSetTarget.materials[0].gallery[0].config.renders['0'].state;
    const excludedFields = isPreview ? excludedStatePreviewMat : excludedStateGalleryMat;
    matSetTarget.materials[0].gallery[0].config.renders['0'].state = mergeState(sourceState, targetState, excludedFields);
  } catch (e) {
    // No existing gallery material set file
    return;
  }
}

export function makeSizeVals(state, isOnLoad=false, preferTexDimsActual=true) {
  var sizeVals = {};
  if (state.texDims) {
    var pixX, pixY;
    if (preferTexDimsActual && state.texDimsActual) {
      pixX = state.texDimsActual[0];
      pixY = state.texDimsActual[1];
    } else {
      pixX = state.texDims[0];
      pixY = state.texDims[1];
      if (state.stretch) {
        pixX = pixX * state.stretch[0];
        pixY = pixY * state.stretch[1];
      }
    }
    sizeVals.pixX = Math.round(pixX);
    sizeVals.pixY = Math.round(pixY);

    if (state.metresPerPixelTextures) {
      var mmX = state.texDims[0] * state.metresPerPixelTextures * 1000;
      var mmY = state.texDims[1] * state.metresPerPixelTextures * 1000;
      if (state.stretch) {
        mmX = mmX * state.stretch[0];
        mmY = mmY * state.stretch[1];
      }
      sizeVals.mmX = Math.round(mmX);
      sizeVals.mmY = Math.round(mmY);
      sizeVals.dpi = Math.round((25.4 / 1000) * (1 / state.metresPerPixelTextures));
    }
  }
  if (state.displacementUnits || state.displacementUnits === 0) {
    sizeVals.dispMm = Math.round(state.displacementUnits * 100000) / 100;
  }

  sizeVals.onLoad = isOnLoad;
  return sizeVals;
}

export function sizeFieldsToState(galleryState, brdfConfig, fields) {
  // Returns true if any state fields changed as a result of the update, false otherwise
  var anyChanged = false;
  if ('dpi' in fields) {
    const mpp = (25.4 / 1000) * (1 / fields['dpi']);
    if (galleryState.metresPerPixelTextures !== mpp) {
      anyChanged = true;
      galleryState.metresPerPixelTextures = mpp;
      galleryState.metresPerPixelTexturesSource = 'User';
    }
    if (brdfConfig) {
      if (brdfConfig.metresPerPixelTextures !== mpp) {
        anyChanged = true;
        brdfConfig.metresPerPixelTextures = mpp;
        brdfConfig.metresPerPixelTexturesSource = 'User';
      }
    }
  } else {
    if ('mmX' in fields) {
      var unstretched = fields['mmx'];
      if (galleryState.stretch) {
        unstretched = (unstretched - 1) / galleryState.stretch[0] + 1;
      }
      const mpp = unstretched / (galleryState.texDims[0] * 1000);
      if (galleryState.metresPerPixelTextures !== mpp) {
        anyChanged = true;
        galleryState.metresPerPixelTextures = mpp;
        galleryState.metresPerPixelTexturesSource = 'User';
      }
      if (brdfConfig) {
        if (brdfConfig.metresPerPixelTextures !== mpp) {
          anyChanged = true;
          brdfConfig.metresPerPixelTextures = mpp;
          brdfConfig.metresPerPixelTexturesSource = 'User';
        }
      }
    }
    if ('mmY' in fields) {
      var unstretched = fields['mmY'];
      if (galleryState.stretch) {
        unstretched = (unstretched - 1) / galleryState.stretch[1] + 1;
      }
      const mpp = unstretched / (galleryState.texDims[1] * 1000);
      if (galleryState.metresPerPixelTextures !== mpp) {
        anyChanged = true;
        galleryState.metresPerPixelTextures = mpp;
        galleryState.metresPerPixelTexturesSource = 'User';
      }
      if (brdfConfig) {
        if (brdfConfig.metresPerPixelTextures !== mpp) {
          anyChanged = true;
          brdfConfig.metresPerPixelTextures = mpp;
          brdfConfig.metresPerPixelTexturesSource = 'User';
        }
      }
    }
  }
  if ('dispMm' in fields) {
    const dispM = fields['dispMm'] / 1000;
    if (galleryState.displacementUnits !== dispM) {
      anyChanged = true;
      galleryState.displacementUnits = dispM;
    }
    if (brdfConfig) {
      if (brdfConfig.displacementUnits !== dispM) {
        anyChanged = true;
        brdfConfig.displacementUnits = dispM;
      }
    }
  }
  return anyChanged;
}

export function updateZoomForChangedMpp(state, oldState) {
  // Given an old and new gallery state with changed MPP value, adjust the zoom values in
  // the new gallery state to correspond with the changed physical size.
  if (state.metresPerPixelTextures && oldState.metresPerPixelTextures && state.zoom) {
    const mppFactor = state.metresPerPixelTextures / oldState.metresPerPixelTextures;
    state.zoom.forEach((f, i, arr) => arr[i] *= mppFactor);
  }
}
